import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { OrderedSet, Seq, Set } from 'immutable';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { CardStatus, UnitStatus } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { selectCardSummariesByControlZoneId } from 'src/store/entityStores/card-selectors';
import { selectUnitSummariesByControlZoneId } from 'src/store/entityStores/unit-selectors';
// import { selectCardSummaryEntitiesByControlZoneId } from 'src/store/generated/card-summary-record.generated';
import { ControlZoneEntityFetchService } from 'src/store/generated/client-sync.generated';
import {
  ControlZoneRecord,
  ControlZoneViewModelBase,
} from 'src/store/generated/control-zone-record.generated';

import { createObservableLookup, ObservableLookup } from 'src/util/lookup';
import { undefinedOrMap } from 'src/util/rxjs/operators';
import { getOrElse } from 'src/util/values';

export class ControlZoneViewModel extends ControlZoneViewModelBase {
  public cardNumbers$: Observable<Set<string>>;
  public cardIdsByStatus: ObservableLookup<typeof CardStatus, Set<string>>;
  public nonHiddenUnitIdsByStatus: ObservableLookup<typeof UnitStatus, Seq.Indexed<string>>;
  public allUnitIdsByStatus: ObservableLookup<typeof UnitStatus, Seq.Indexed<string>>;
  public allUnitIds$: Observable<Set<string>>;
  public nonHiddenUnitIds$: Observable<Set<string>>;
  public notificationIds$: Observable<OrderedSet<string>>;
  constructor(controlZone: ControlZoneRecord, store: Store<AppState>) {
    super(controlZone);
    this.cardNumbers$ = store.select(selectCardSummariesByControlZoneId(this.id)).pipe(
      distinctUntilChanged(),
      map((cards) => cards.map((card) => card.cardNumber).toSet()),
      distinctUntilChanged(),
    );
    this.cardIdsByStatus = createObservableLookup(CardStatus, (cardStatus) =>
      store.select(selectCardSummariesByControlZoneId(this.id)).pipe(
        distinctUntilChanged(),
        map((cards) =>
          cards
            .filter((r) => r.status === cardStatus)
            .sort((a, b) => {
              return a.dateCreated < b.dateCreated ? -1 : 1;
            })
            .map((c) => c.id)
            .toSet(),
        ),
        distinctUntilChanged(),
      ),
    );

    this.allUnitIds$ = store.select(selectUnitSummariesByControlZoneId(controlZone.id)).pipe(
      distinctUntilChanged(),
      map((units) => units.map((u) => u.id).toSet()),
      distinctUntilChanged(),
    );
    this.nonHiddenUnitIds$ = store
      .select(selectUnitSummariesByControlZoneId(controlZone.id, false))
      .pipe(
        distinctUntilChanged(),

        map((units) =>
          units
            .filter((u) => getOrElse(u.isHidden, false) === false)
            .map((u) => u.id)
            .toSet(),
        ),
        distinctUntilChanged(),
      );

    this.nonHiddenUnitIdsByStatus = createObservableLookup(UnitStatus, (unitStatus) =>
      store.select(selectUnitSummariesByControlZoneId(controlZone.id, false)).pipe(
        distinctUntilChanged(),
        map((units) =>
          units
            .filter((u) => !getOrElse(u.isHidden, false))
            .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation))
            .filter((u) => u.status === unitStatus)
            .map((u) => u.id),
        ),
        distinctUntilChanged(),
      ),
    );

    this.allUnitIdsByStatus = createObservableLookup(UnitStatus, (unitStatus) =>
      store.select(selectUnitSummariesByControlZoneId(controlZone.id)).pipe(
        distinctUntilChanged(),
        map((units) =>
          units
            .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation))
            .filter((u) => u.status === unitStatus)
            .map((u) => u.id),
        ),
        distinctUntilChanged(),
      ),
    );
  }
}

@Injectable({ providedIn: 'root' })
export class ControlZoneViewModelService {
  constructor(
    private readonly _controlZoneFetchService: ControlZoneEntityFetchService,
    private _store: Store<AppState>,
  ) {}

  public getById(id: string): Observable<ControlZoneViewModel | undefined> {
    return this._controlZoneFetchService
      .getById(id)
      .pipe(undefinedOrMap((controlZone) => new ControlZoneViewModel(controlZone, this._store)));
  }
}
